import "@/App.css";
import type { ReactNode } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  type RouteObject,
} from "react-router-dom";
import { CourseDetailPage } from "@/pages/CourseDetailPage";
import { CourseCompletionQuizIntroPage } from "@/pages/CourseCompletionQuizIntroPage";
import { CourseCompletionQuizResultPage } from "@/pages/CourseCompletionQuizResultPage";
import { CourseCompletionQuizRunPage } from "@/pages/CourseCompletionQuizRunPage";
import { DashboardGateway } from "@/pages/DashboardGateway";
import { RootRedirect } from "@/pages/RootRedirect";
import { ErrorPage } from "@/pages/ErrorPage";
import { LegalNoticePage } from "@/pages/LegalNoticePage";
import type { LegalNoticeId } from "@/components/legal/legalNotices";
import { AppLayout } from "@/components/layout/AppLayout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";

const errorElement = (
  <AppLayout>
    <ErrorPage />
  </AppLayout>
);

const appRoute = (path: string, element: ReactNode): RouteObject => ({
  path,
  element: <AppLayout>{element}</AppLayout>,
  errorElement,
});

const protectedAppRoute = (path: string, element: ReactNode): RouteObject => ({
  path,
  element: (
    <ProtectedRoute>
      <AppLayout>{element}</AppLayout>
    </ProtectedRoute>
  ),
  errorElement,
});

const legalNoticeRoute = (path: string, notice: LegalNoticeId): RouteObject =>
  appRoute(path, <LegalNoticePage notice={notice} />);

const throwNotFound = () => {
  throw new Response(null, { status: 404, statusText: "Not Found" });
};

export const appRoutes: RouteObject[] = [
  appRoute(
    "/",
    <ProtectedRoute requireAuth={false}>
      <RootRedirect />
    </ProtectedRoute>
  ),
  legalNoticeRoute("/preview/:courseExternalId/datenschutz", "datenschutz"),
  legalNoticeRoute("/preview/:courseExternalId/impressum", "impressum"),
  protectedAppRoute(
    "/preview/:courseExternalId/:nodeId/:contentElementId?",
    <CourseDetailPage />
  ),
  protectedAppRoute("/preview/:courseExternalId", <CourseDetailPage />),
  // Former trainer dashboard URL, kept so existing links still work.
  protectedAppRoute(
    "/course/:enrollmentId/dashboard-trainer",
    <Navigate to="../dashboard" relative="path" replace />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/trainer-preview/:nodeId/:contentElementId?",
    <CourseDetailPage />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/trainer-preview",
    <CourseDetailPage />
  ),
  protectedAppRoute("/course/:enrollmentId/dashboard", <DashboardGateway />),
  protectedAppRoute(
    "/course/:enrollmentId/final-quiz/run",
    <CourseCompletionQuizRunPage />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/final-quiz/result",
    <CourseCompletionQuizResultPage />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/final-quiz",
    <CourseCompletionQuizIntroPage />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/datenschutz",
    <LegalNoticePage notice="datenschutz" />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/impressum",
    <LegalNoticePage notice="impressum" />
  ),
  protectedAppRoute(
    "/course/:enrollmentId/:nodeId/:contentElementId?",
    <CourseDetailPage />
  ),
  protectedAppRoute("/course/:enrollmentId", <CourseDetailPage />),
  legalNoticeRoute("/impressum", "impressum"),
  {
    path: "/up",
    element: <div>OK</div>,
  },
  {
    path: "*",
    loader: throwNotFound,
    element: <AppLayout>{null}</AppLayout>,
    errorElement,
    hydrateFallbackElement: <></>,
  },
];

const router = createBrowserRouter(appRoutes);

const App = () => {
  return <RouterProvider router={router} />;
};

export default App;
